import React from "react";
import { ChevronDownIcon, ChevronUpIcon, BookmarkIcon } from "lucide-react";
import { Checkbox } from "../../../../../../../../components/ui";
import type {
  Module,
  SubModule,
  TlmTopic,
} from "../../../../../../../../services/school/crm/curriculum/useGetCurriculumManagementQuery";
import { useGradeManagementInfo } from "./useGradeManagementInfo";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { LOGIN_ROLES } from "@/lib/utils";

const GradeManagementInfo = () => {
  const {
    curriculumData,
    expandedGrades,
    expandedModules,
    expandedSubModules,
    isLoading,
    error,
    handleGradeCheckboxChange,
    handleModuleCheckboxChange,
    handleSubModuleCheckboxChange,
    handleTlmTopicCheckboxChange,
    toggleGradeExpansion,
    toggleModuleExpansion,
    toggleSubModuleExpansion,
    collapseAll,
    saveChanges,
  } = useGradeManagementInfo();

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
  const roles = loggedInUserData?.role || [];

  // Helper to check if user has a given role
  const hasRole = (role: string) =>
    Array.isArray(roles) && roles.includes(role);

  const normalizeChecked = (checked: boolean | "indeterminate") =>
    checked === true;

  if (isLoading) {
    return (
      <div className="bg-white p-5">
        <h2 className="text-[18px] text-primary mb-5">Grade Management</h2>
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">Loading curriculum data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-5">
        <h2 className="text-[18px] text-primary mb-5">Grade Management</h2>
        <div className="flex items-center justify-center py-8">
          <div className="text-red-500">Error loading curriculum data</div>
        </div>
      </div>
    );
  }

  const restrictedRoles = [LOGIN_ROLES.SALES_OPS, LOGIN_ROLES.INVENTORY_TEAM, LOGIN_ROLES.LABS_TEAM, LOGIN_ROLES.FINANCE_TEAM];
  const canShowSaveButton = !restrictedRoles.some(role => hasRole(role));

  return (
    <div className="bg-white p-5">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-[18px] text-primary">Grade Management</h2>
        <button
          onClick={collapseAll}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer"
        >
          <span>Collapse</span>
          <ChevronDownIcon className="w-4 h-4 " />
        </button>
      </div>

      <div className="space-y-2">
        {Array.isArray(curriculumData) &&
          curriculumData.map((grade) => (
            <div
              key={grade?.class_id}
              className={`border rounded-lg ${
                expandedGrades.has(grade?.class_id) ? "bg-white" : "bg-gray-100"
              }`}
            >
              {/* Grade Level */}
              <div
                className={`p-4 rounded-t-lg ${
                  expandedGrades.has(grade?.class_id)
                    ? "bg-gray-50"
                    : "bg-gray-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={grade.isEligible}
                      onCheckedChange={(checked) =>
                        handleGradeCheckboxChange(
                          grade.class_id,
                          normalizeChecked(checked)
                        )
                      }
                      className="data-[state=checked]:bg-primary data-[state=checked]:border-orange-500"
                      disabled={hasRole("INVENTORY_TEAM")}
                    />
                    <span className="font-bold text-lg text-black">
                      {grade?.curriculum?.curriculum_name} - {grade?.year}
                    </span>
                  </div>
                  <div className="flex">
                    {canShowSaveButton && (
                      <button
                        onClick={saveChanges}
                        className="mr-5 px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors cursor-pointer"
                        title="Save Changes"
                      >
                        Save
                      </button>
                    )}
                    <button
                      onClick={() => toggleGradeExpansion(grade.class_id)}
                      className="flex items-center gap-2 text-primary hover:text-primary/80"
                    >
                      {expandedGrades.has(grade.class_id) ? (
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center cursor-pointer">
                          <ChevronUpIcon className="w-4 h-4 text-white" />
                        </div>
                      ) : (
                        <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center cursor-pointer">
                          <ChevronDownIcon className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Modules */}
              {expandedGrades.has(grade.class_id) && (
                <div className="p-4 bg-orange-50 rounded-b-lg">
                  <div className="space-y-3">
                    {grade.modules.map((module: Module) => (
                      <div key={module.module_id} className="ml-4">
                        <div className="flex items-center justify-between py-2">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={module.isEligible}
                              onCheckedChange={(checked: any) =>
                                handleModuleCheckboxChange(
                                  grade.class_id,
                                  module.module_id,
                                  normalizeChecked(checked)
                                )
                              }
                              className="data-[state=checked]:bg-primary data-[state=checked]:border-orange-500"
                              disabled={hasRole("INVENTORY_TEAM")}
                            />
                            <span className="font-medium">
                              {module.module_name}
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              toggleModuleExpansion(
                                grade?.class_id,
                                module.module_id
                              )
                            }
                            className="flex items-center gap-2 text-primary hover:text-primary/80"
                          >
                            {expandedModules.has(
                              `${grade.class_id}-${module.module_id}`
                            ) ? (
                              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center cursor-pointer">
                                <ChevronUpIcon className="w-4 h-4 text-white" />
                              </div>
                            ) : (
                              <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center cursor-pointer">
                                <ChevronDownIcon className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </button>
                        </div>
                        <hr />
                        {/* Sub Modules */}
                        {expandedModules.has(
                          `${grade.class_id}-${module.module_id}`
                        ) && (
                          <div className="ml-6 space-y-2">
                            {module.sub_modules.map(
                              (subModule: SubModule, index: number) => (
                                <div key={subModule.sub_module_id}>
                                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                                    <div className="flex items-center gap-3">
                                      <Checkbox
                                        checked={subModule.isEligible}
                                        onCheckedChange={(checked: any) =>
                                          handleSubModuleCheckboxChange(
                                            grade.class_id,
                                            module.module_id,
                                            subModule.sub_module_id,
                                            normalizeChecked(checked)
                                          )
                                        }
                                        className="data-[state=checked]:bg-primary data-[state=checked]:border-orange-500"
                                      />
                                      <span>{subModule.sub_module_name}</span>
                                    </div>
                                    <button
                                      onClick={() =>
                                        toggleSubModuleExpansion(
                                          grade.class_id,
                                          module.module_id,
                                          subModule.sub_module_id
                                        )
                                      }
                                      className="flex items-center gap-2 text-primary hover:text-primary/80"
                                    >
                                      {expandedSubModules.has(
                                        `${grade.class_id}-${module.module_id}-${subModule.sub_module_id}`
                                      ) ? (
                                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center cursor-pointer">
                                          <ChevronUpIcon className="w-4 h-4 text-white" />
                                        </div>
                                      ) : (
                                        <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center cursor-pointer">
                                          <ChevronDownIcon className="w-4 h-4 text-white" />
                                        </div>
                                      )}
                                    </button>
                                  </div>
                                  <hr />
                                  {/* TLM Topics */}
                                  {expandedSubModules.has(
                                    `${grade.class_id}-${module.module_id}-${subModule.sub_module_id}`
                                  ) && (
                                    <div className="ml-6 mt-3">
                                      <div className="grid grid-cols-2 gap-4">
                                        {subModule.tlm_topics.map(
                                          (topic: TlmTopic) => (
                                            <div
                                              key={topic.tlmMasterId}
                                              className="flex items-center gap-3"
                                            >
                                              <Checkbox
                                                checked={topic.isEligible}
                                                onCheckedChange={(
                                                  checked: any
                                                ) =>
                                                  handleTlmTopicCheckboxChange(
                                                    grade.class_id,
                                                    module.module_id,
                                                    subModule.sub_module_id,
                                                    topic.tlmMasterId,
                                                    normalizeChecked(checked)
                                                  )
                                                }
                                                className="data-[state=checked]:bg-primary data-[state=checked]:border-orange-500"
                                              />
                                              <span className="text-sm">
                                                {topic.curriculumTopic}
                                              </span>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default GradeManagementInfo;
